import { z } from 'zod'
import { MaintenanceOrder } from '../../../../../../types/service.types'
import { validateEquipmentId } from '../../../../components/NewDrawers/consts/searchDrawer.consts'

export const finishEquipmentSchema = z
  .object({
    receiverSerialNumber: z.string(),
    replacementType: z.string(),
    replacedReceiverModelId: z.string().optional(),
    replacedReceiverSerialNumber: z
      .string()
      .optional()
      .refine((val) => !val || val.length === 23),
    replacedHasCas: z.boolean(),
    replacedSmartCardId: z
      .string()
      .nullable()
      .refine((val) => !val, {
        message: 'Ошибка встроенной карты',
      }),
    replacedEquipmentId: z
      .string()
      .optional()
      .refine((val) => !val || validateEquipmentId(val)),
  })
  .superRefine((data, ctx) => {
    const type = data.replacementType as ReplacementType

    const cleanSn = data.receiverSerialNumber.replace(/\s/g, '')
    if (cleanSn.length !== 23) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['receiverSerialNumber'],
        message: 'S/N должен содержать 23 символа',
      })
    }

    if (
      type === 'Замена только приемника' &&
      !data.replacedReceiverSerialNumber
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['replacedReceiverSerialNumber'],
        message: 'Обязательное поле',
      })
    }

    if (type === 'Замена только смарт-карты' && !data.replacedEquipmentId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['replacedEquipmentId'],
        message: 'Обязательное поле',
      })
    }

    if (type === 'Замена приемника и смарт-карты') {
      if (!data.replacedEquipmentId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['replacedEquipmentId'],
          message: 'Обязательное поле',
        })
      }
      if (!data.replacedReceiverSerialNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['replacedReceiverSerialNumber'],
          message: 'Обязательное поле',
        })
      }
    }
  })

export type FinishEquipmentForm = z.infer<typeof finishEquipmentSchema>

export const getFinishDefaultValues = (
  request?: MaintenanceOrder,
): FinishEquipmentForm => ({
  receiverSerialNumber:
    request?.receiverSerialNumber ?? request?.primaryReceiverSerialNumber ?? '',
  replacedReceiverModelId: request?.replacedReceiverProduct ?? '',
  replacedReceiverSerialNumber: request?.replacedReceiverSerialNumber ?? '',
  replacedEquipmentId: request?.oldEquipmentSerialNumber ?? '',
  replacedHasCas: false,
  replacedSmartCardId: null,
  replacementType: getReplacementType(request),
})

export const changeEquipmentSchema = z
  .object({
    equipmentId: z.string().refine((val) => !!val && validateEquipmentId(val), {
      message: 'Неверный формат id оборудования',
    }),
    receiverSerialNumber: z.string().refine((val) => !val || val.length === 23),
    receiverModelId: z.string(),
    replacementType: z.string(),
    // если есть встроенная смарт-карта - ошибка
    smartCardId: z
      .string()
      .nullable()
      .refine((val) => !val, {
        message: 'Ошибка встроенной карты',
      }),
    hasCas: z.boolean(),
    replacedReceiverModelId: z.string().optional(),
    replacedReceiverSerialNumber: z
      .string()
      .optional()
      .refine((val) => !val || val.length === 23),
    replacedHasCas: z.boolean(),
    replacedSmartCardId: z
      .string()
      .nullable()
      .refine((val) => !val, {
        message: 'Ошибка встроенной карты',
      }),
    replacedEquipmentId: z
      .string()
      .optional()
      .refine((val) => !val || validateEquipmentId(val)),
  })
  .superRefine((data, ctx) => {
    const type = data.replacementType as ReplacementType

    if (
      type === 'Замена только приемника' &&
      !data.replacedReceiverSerialNumber
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['replacedReceiverSerialNumber'],
        message: 'Обязательное поле',
      })
    }

    if (type === 'Замена только смарт-карты' && !data.replacedEquipmentId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['replacedEquipmentId'],
        message: 'Обязательное поле',
      })
    }

    if (type === 'Замена приемника и смарт-карты') {
      if (!data.replacedEquipmentId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['replacedEquipmentId'],
          message: 'Обязательное поле',
        })
      }
      if (!data.replacedReceiverSerialNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['replacedReceiverSerialNumber'],
          message: 'Обязательное поле',
        })
      }
    }
  })

export type ChangeEquipmentForm = z.infer<typeof changeEquipmentSchema>

export const getDefaultValues = (
  request?: MaintenanceOrder,
): ChangeEquipmentForm => ({
  equipmentId:
    (request?.oldEquipmentSerialNumber
      ? request.oldEquipmentSerialNumber
      : request?.equipmentId) ?? '',
  hasCas: request?.hasCas ?? false,
  smartCardId: null,
  receiverSerialNumber:
    request?.receiverSerialNumber ?? request?.primaryReceiverSerialNumber ?? '',
  receiverModelId:
    request?.receiverProduct || request?.primaryReceiverProduct
      ? (request?.receiverProduct ?? request?.primaryReceiverProduct!)
      : '',
  replacedReceiverModelId: request?.replacedReceiverProduct ?? '',
  replacedReceiverSerialNumber: request?.replacedReceiverSerialNumber ?? '',
  replacedEquipmentId: request?.oldEquipmentSerialNumber
    ? (request?.equipmentId ?? '')
    : '',
  replacedHasCas: request?.replacedHasCas ?? false,
  replacedSmartCardId: null,
  replacementType: getReplacementType(request),
})

export const getReplacementType = (
  request: MaintenanceOrder | undefined,
): ReplacementType => {
  if (
    request?.oldEquipmentSerialNumber &&
    request?.replacedReceiverSerialNumber
  )
    return 'Замена приемника и смарт-карты'
  else if (request?.oldEquipmentSerialNumber) return 'Замена только смарт-карты'
  else if (request?.replacedReceiverSerialNumber)
    return 'Замена только приемника'

  return 'Не было замены'
}

export type ReplacementType =
  | 'Замена только смарт-карты'
  | 'Замена только приемника'
  | 'Замена приемника и смарт-карты'
  | 'Не было замены'

export const replacementTypes: ReplacementType[] = [
  'Замена только смарт-карты',
  'Замена только приемника',
  'Замена приемника и смарт-карты',
  'Не было замены',
]

export const getReplacementValue = (type: ReplacementType): number => {
  if (type === 'Замена приемника и смарт-карты') return 4
  else if (type === 'Замена только смарт-карты') return 2
  else if (type === 'Замена только приемника') return 3

  return 1
}
